import { useContext, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { UserContext } from 'op-contexts';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { PatientNavOptionType } from './ROSidePatientNav';

export const GET_NIRVANA_LINK_AVAILABLE = gql`
  query nirvanaLinkAvailable($patientId: ID!) {
    patient(id: $patientId) {
      id
      nirvanaLinkAvailable
    }
  }
`;

interface NirvanaLinkPatient {
  id: string;
  nirvanaLinkAvailable: boolean | null;
}

// Feature-flagged, backend-gated "Open Canvas" nav entry (external, new tab).
// Returns null unless this deployment + patient can actually launch.
export const useOpenCanvasNavOption = (patientId: string | undefined): PatientNavOptionType | null => {
  const { hasFeature } = useContext(UserContext);
  const enabled = Boolean(hasFeature('LinkToNirvana')) && Boolean(patientId);
  const { data } = useQuery<{ patient: NirvanaLinkPatient | null }>(GET_NIRVANA_LINK_AVAILABLE, {
    variables: { patientId },
    skip: !enabled,
    // Authorization-sensitive gate: a cached true would keep the entry visible
    // after the tenant toggle or the user's role changes mid-session.
    fetchPolicy: 'network-only',
  });
  const available = Boolean(data?.patient?.nirvanaLinkAvailable);
  // Stable identity: this feeds ROSidePatientNav's menu-option useMemo chain.
  return useMemo(() => {
    if (!enabled || !available) return null;
    return {
      icon: <OpenInNewIcon />,
      label: 'Canvas',
      linkTo: `/sso/nirvana_redirect?patient=${patientId}`,
      id: 'open-canvas',
      disabled: false,
      isExternal: true,
      isVisible: true,
    };
  }, [enabled, available, patientId]);
};
